import {
    Checkbox,
    createStyles,
    FormLabel,
    Link,
    SnackbarContent,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import amber from "@material-ui/core/colors/amber";
import { Info } from "@material-ui/icons/";
import {
    CardCvcElement,
    CardExpiryElement,
    CardNumberElement,
    ElementsConsumer
} from "@stripe/react-stripe-js";
import React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import LoaderButton from "../../shared/LoaderButton";
import StripeElementWrapper from "./StripeElementWrapper";
import Constants from "../../../models/utility/Constants";
import { StripeElements, Stripe as StripeJS } from "@stripe/stripe-js";

const WhiteTextTypography = withStyles({
    root: {
        color: "#FFFFFF"
    }
})(Typography) as typeof Typography;
const WhiteFormLabel = withStyles({
    root: {
        color: "#CCCCCC"
    }
})(FormLabel);
const styles = (theme: Theme) =>
    createStyles({
        acceptTermsElement: {
            marginBottom: theme.spacing(2),
            marginTop: theme.spacing(2),
            paddingTop: theme.spacing(2),
            display: "inline-flex"
        },
        button: {
            marginTop: theme.spacing(5),
            color: theme.palette.common.white
        },
        formElement: {
            marginBottom: theme.spacing(),
            marginTop: theme.spacing(2),
            paddingTop: theme.spacing(2),
            color: "white"
        },
        formElementHalf: {
            "&:first-child": { marginRight: theme.spacing() },
            "&:last-child": { marginLeft: theme.spacing() },
            marginBottom: theme.spacing(),
            marginTop: theme.spacing(2),
            paddingTop: theme.spacing(2),
            color: theme.palette.common.white,
            input: {
                color: theme.palette.common.black,
                backgroundColor: "rgba(248, 179, 50, 1)"
            },
            floatingLabel: {
                color: "#CCCCCC"
            }
        },
        sectionHeaderParagraph: {
            borderBottom: `4px solid ${theme.palette.primary.main}`,
            marginTop: theme.spacing(4)
        },
        termsLink: { cursor: "pointer" },
        warningContainer: {
            backgroundColor: amber[600],
            display: "block",
            marginTop: theme.spacing(2),
            maxWidth: "100%",
            verticalAlign: "top",
            width: "100%"
        },
        warningIcon: {
            color: "#424242",
            marginRight: theme.spacing(),
            opacity: 0.9
        },
        warningMessage: {
            alignItems: "flex-start",
            color: "black",
            display: "flex"
        },
        sidebyside: {
            display: "inline-flex"
        },
        input: {
            color: theme.palette.common.black,
            backgroundColor: "rgba(248, 179, 50, 1)"
        },
        floatingLabel: {
            color: "#CCCCCC"
        }
    });

interface ICreditCardInputState {
    acceptTerms: boolean;
    submitted: boolean;
}

/** Props common without injecting Stripe */
interface ICreditCardInputProps {
    captchaKey: string;
    onRegister: (creditCardToken: string) => void;
    onCreditCardError: (errorMessage: string) => void;
    handleTermsChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleCaptchaReceived: (captcha: string | null) => void;
    registerDisabled: boolean;
}

/** Props associated with Stripe */
interface ICreditCardInputWithStripe extends ICreditCardInputProps {
    elements: StripeElements | null;
    stripe: StripeJS | null;
}

/** Internally the wrapped class component accepts the base, Stripe and style props. */
type CreditCardInputPropsWithStripeAndStyles = ICreditCardInputWithStripe &
    WithStyles<typeof styles>;

class CreditCardInput extends React.PureComponent<
    CreditCardInputPropsWithStripeAndStyles,
    ICreditCardInputState
> {
    private reCaptchaRef: React.RefObject<ReCAPTCHA>;

    constructor(props: CreditCardInputPropsWithStripeAndStyles) {
        super(props);
        this.state = {
            acceptTerms: false,
            submitted: false
        };

        this.reCaptchaRef = React.createRef();
    }

    public render() {
        const classes = this.props.classes;
        const registerDisabled =
            this.props.registerDisabled || this.state.submitted;

        /** Attempt to render the reCAPTCHA only after the captchaKey is set as it may initally be undefined. */
        const recaptchaComponent = this.props.captchaKey ? (
            <ReCAPTCHA
                theme="light"
                size="invisible"
                ref={this.reCaptchaRef}
                sitekey={this.props.captchaKey}
                onChange={this.handleChange}
            />
        ) : null;

        return (
            <div>
                <WhiteTextTypography
                    className={classes.sectionHeaderParagraph}
                    component="p"
                    variant="h4"
                >
                    Authorize Your Account
                </WhiteTextTypography>
                <SnackbarContent
                    className={classes.warningContainer}
                    message={
                        <span
                            id="client-snackbar"
                            className={classes.warningMessage}
                        >
                            <Info className={classes.warningIcon} />
                            <div className={classes.input}>
                                The process involves authorizing a valid credit
                                card.{" "}
                                <b>
                                    You will <u>NOT</u> be charged at this time.
                                </b>
                                This is for authentication purposes and we only
                                check that this is a valid credit card now. It
                                is only if you are in breach of the Terms &
                                Conditions that a charge will be made against
                                the card.
                            </div>
                        </span>
                    }
                />
                <div>
                    <StripeElementWrapper
                        className={classes.formElement}
                        id={"cardNumber"}
                        label="Card Number"
                        placeholder=""
                        component={CardNumberElement}
                    />
                    <div style={{ display: "flex", color: "white" }}>
                        <StripeElementWrapper
                            className={classes.formElementHalf}
                            id={"cvc"}
                            label="CVC"
                            placeholder=""
                            component={CardCvcElement}
                        />
                        <StripeElementWrapper
                            className={classes.formElementHalf}
                            id={"expiry"}
                            label="Expiry"
                            placeholder="MM / YY"
                            component={CardExpiryElement}
                        />
                    </div>
                </div>
                <div className={classes.acceptTermsElement}>
                    <div className={classes.sidebyside}>
                        <Checkbox
                            checked={this.state.acceptTerms}
                            onChange={this.handleTermsChange}
                            color="primary"
                        />
                    </div>
                    <div className={classes.sidebyside}>
                        <WhiteFormLabel>
                            I have read, understand and agree to be bound by the{" "}
                            <Link
                                className={classes.termsLink}
                                href="https://www.clhbid.com/how-clhbid-works/clhbid-bidding-terms-and-conditions/"
                                target="_blank"
                            >
                                TERMS AND CONDITIONS.
                            </Link>{" "}
                            I understand by accepting these terms, I am entering
                            into a contractual obligation with CLHbid.com.
                            <Link
                                className={classes.termsLink}
                                href="https://www.clhbid.com/how-clhbid-works/clhbid-bidding-terms-and-conditions/"
                                target="_blank"
                            >
                                {" "}
                                Click to review TERMS AND CONDITIONS
                            </Link>
                        </WhiteFormLabel>
                    </div>
                </div>
                {recaptchaComponent}
                <div className={classes.button}>
                    <LoaderButton
                        loading={registerDisabled}
                        type="button"
                        size="large"
                        variant="contained"
                        onClick={this.onSubmit}
                    >
                        Register
                    </LoaderButton>
                </div>
            </div>
        );
    }

    private handleChange = (value: string | null) => {
        this.props.handleCaptchaReceived(value);
        if (this.state.submitted) {
            this.registerCard();
            this.setState({ submitted: false });
        }
    };

    private handleTermsChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        this.setState({ acceptTerms: event.target.checked });
        this.props.handleTermsChange(event);
    };

    private onSubmit = (e: React.MouseEvent) => {
        e.preventDefault();

        this.setState({ submitted: true }, () => {
            if (this.reCaptchaRef && this.reCaptchaRef.current) {
                this.reCaptchaRef.current.reset();
                this.reCaptchaRef.current.execute();
            }
        });
    };

    private registerCard = async () => {
        // Check if stripe is initialized, and error if it is not
        if (!this.props.stripe || !this.props.elements) {
            this.props.onCreditCardError("Stripe is not initialized.");
            return;
        }

        const ccElement = this.props.elements.getElement(CardNumberElement);
        if (!ccElement) {
            this.props.onCreditCardError("Credit card element not found.");
            return;
        }

        const response = await this.props.stripe.createToken(ccElement, {
            currency: Constants.StripeTokenCurrencyCAD
        });

        if (response.token) {
            const cardToken = response.token.id;
            this.props.onRegister(cardToken);
        } else {
            const errorMsg =
                response.error && response.error.message
                    ? response.error.message
                    : "An unknown error occurred.";
            this.props.onCreditCardError(errorMsg);
        }
    };
}

const CreditCardInputWithStyles = withStyles(styles)(CreditCardInput);

const CreditCardInputWithStripe = (args: ICreditCardInputProps) => (
    <ElementsConsumer>
        {({ stripe, elements }) =>
            !stripe || !elements ? (
                <p>"Loading Stripe..."</p>
            ) : (
                <CreditCardInputWithStyles
                    stripe={stripe}
                    elements={elements}
                    {...args}
                />
            )
        }
    </ElementsConsumer>
);

export default CreditCardInputWithStripe;
